import { Tono } from '../components/kit'
import { formatoDia } from './fecha'
import {
  Bien, EstadoBien, EstadoFisico, EstadoPlancheta, EstadoSolicitud, Movimiento, PersonaMini, RecintoMini, Revisar, Sector, TipoBien,
} from '../types/inventario'

/*
  Un solo lugar para cómo se nombra y qué tono tiene cada estado del inventario
  (DESIGN_KIT.md: estado del dominio → tono en un solo mapa).
*/

export const PLANCHETA: Record<EstadoPlancheta, { texto: string; tono: Tono }> = {
  DESACTUALIZADA: { texto: 'Desactualizada', tono: 'error' },
  CON_MODIFICACIONES: { texto: 'Con modificaciones', tono: 'aviso' },
  ACTUALIZADA: { texto: 'Actualizada', tono: 'exito' },
}

export const TIPO: Record<TipoBien, { texto: string; corto: string; tono: Tono }> = {
  INVENTARIABLE: { texto: 'Inventariable', corto: 'Inventariable', tono: 'marca' },
  NO_INVENTARIABLE: { texto: 'No inventariable', corto: 'No inv.', tono: 'neutro' },
  OTRO: { texto: 'Otro', corto: 'Otro', tono: 'info' },
  '': { texto: 'Sin tipo', corto: 'Sin tipo', tono: 'aviso' },
}

export const ESTADO: Record<EstadoBien, { texto: string; tono: Tono }> = {
  ACTIVO: { texto: 'Activo', tono: 'neutro' },
  NO_UBICADO: { texto: 'No ubicado', tono: 'error' },
  BAJA: { texto: 'Para dar de baja', tono: 'aviso' },
}

export const ESTADO_FISICO: Record<EstadoFisico, { texto: string; tono: Tono }> = {
  BUENO: { texto: 'Bueno', tono: 'exito' },
  REGULAR: { texto: 'Regular', tono: 'aviso' },
  MALO: { texto: 'Malo', tono: 'error' },
  '': { texto: 'Sin evaluar', tono: 'neutro' },
}

export const SECTOR: Record<Sector, string> = { NORTE: 'Norte', PONIENTE: 'Poniente' }

export const ESTADO_SOLICITUD: Record<EstadoSolicitud, { texto: string; tono: Tono }> = {
  PENDIENTE: { texto: 'Pendiente', tono: 'aviso' },
  APROBADA: { texto: 'Aprobada', tono: 'exito' },
  RECHAZADA: { texto: 'Rechazada', tono: 'error' },
  ANULADA: { texto: 'Anulada', tono: 'neutro' },
}

export const REVISAR: Record<Revisar, string> = {
  repetidos: 'N° repetido',
  sin_numero: 'Sin N°',
  sin_ubicacion: 'Sin ubicación',
  etiqueta_pendiente: 'Etiqueta pendiente',
  no_ubicado: 'No ubicado',
  sin_foto: 'Sin foto',
}

/** '3 fotos', '1 foto', 'Sin fotos'. */
export const cuantasFotos = (n: number) => (n === 0 ? 'Sin fotos' : `${n} ${n === 1 ? 'foto' : 'fotos'}`)

/** 'Edificio Norte · piso 3'. */
export function ubicacion(r: Pick<RecintoMini, 'sector' | 'piso'>): string {
  return `Edificio ${SECTOR[r.sector]}${r.piso != null ? ` · piso ${r.piso}` : ''}`
}

/** '  dcc-0058 ' → 'DCC-0058': como compara el backend (inventario.normalizar_numero). */
export function normalizarNumero(texto: string): string {
  return texto.trim().replace(/\s+/g, ' ').toUpperCase()
}

/**
 * El tipo que dice el formato del N° (core/models.py, `tipo_de_numero`): `12345-1`
 * inventariable, `971` no inventariable, `DCC-0001` otro. `null` = formato
 * desconocido (el backend lo rechaza).
 */
export function tipoDeNumero(numero: string): TipoBien | null {
  const n = normalizarNumero(numero)
  if (!n) return ''
  if (/^DCC-\d+$/.test(n)) return 'OTRO'
  if (/^\d+-\d+$/.test(n)) return 'INVENTARIABLE'
  if (/^\d+$/.test(n)) return 'NO_INVENTARIABLE'
  return null
}

/** 'DESCRIPCIÓN · MARCA · MODELO' sin los vacíos ni el 'S/M' de la planilla. */
export function marcaModelo(b: Pick<Bien, 'marca' | 'modelo'>): string {
  return [b.marca, b.modelo].filter((x) => x && x !== 'S/M').join(' · ')
}

const ORDEN_NUMERO = new Intl.Collator('es', { numeric: true })
const ORDEN_TIPO: TipoBien[] = ['INVENTARIABLE', 'NO_INVENTARIABLE', 'OTRO', '']

/** Orden de la plancheta: inventariables, no inventariables, otros; cada grupo por N° (10953-2 antes que 10953-10). */
export function ordenarParaPlancheta<T extends Pick<Bien, 'tipo' | 'numero'>>(bienes: T[]): T[] {
  return [...bienes].sort((a, b) => ORDEN_TIPO.indexOf(a.tipo) - ORDEN_TIPO.indexOf(b.tipo) || ORDEN_NUMERO.compare(a.numero, b.numero))
}

// --- Historial -------------------------------------------------------------------

/** Nombre legible de cada campo (historial y vista previa de planillas). */
export const CAMPOS: Record<string, string> = {
  numero: 'N°',
  tipo: 'Tipo',
  descripcion: 'Descripción',
  marca: 'Marca',
  modelo: 'Modelo',
  serie: 'N° de serie',
  estado: 'Estado',
  estado_fisico: 'Estado físico',
  etiqueta_pendiente: 'Etiqueta pendiente',
  observacion: 'Observación',
  nombre: 'Nombre',
  piso: 'Piso',
  activo: 'Vigente',
  codigo: 'Código',
  plancheta_estado: 'Plancheta',
  oficina: 'Oficina',
  direccion: 'Dirección',
  comuna: 'Comuna',
  ciudad: 'Ciudad',
  email: 'Correo',
}

/** Un valor del historial o de una planilla, legible ('MALO' → 'Malo', true → 'Sí'). */
export function valor(campo: string, v: unknown): string {
  if (v === null || v === undefined || v === '') return campo === 'estado_fisico' ? ESTADO_FISICO[''].texto : '—'
  if (typeof v === 'boolean') return v ? 'Sí' : 'No'
  const texto = String(v)
  if (campo === 'tipo') return TIPO[texto as TipoBien]?.texto ?? texto
  if (campo === 'estado') return ESTADO[texto as EstadoBien]?.texto ?? texto
  if (campo === 'estado_fisico') return ESTADO_FISICO[texto as EstadoFisico]?.texto ?? texto
  if (campo === 'plancheta_estado') return PLANCHETA[texto as EstadoPlancheta]?.texto ?? texto
  if (campo === 'encargado') return (v as PersonaMini).nombre ?? '—'
  return texto
}

export interface Descripcion {
  titulo: string
  /** 'Descripción: SILLA → SILLA GIRATORIA', una por campo que cambió. */
  detalle: string[]
}

/**
 * Un movimiento en palabras. En la ficha de una oficina (`enRecinto`) se nombra el bien
 * y un traslado se lee como entrada o salida; en la ficha de un bien, no hace falta.
 */
export function describirMovimiento(m: Movimiento, enRecinto: boolean): Descripcion {
  const bien = enRecinto && m.bien ? `${m.bien.numero || 'Sin N°'} ${m.bien.descripcion}` : ''
  const conBien = (texto: string) => (bien ? `${bien}: ${texto.charAt(0).toLowerCase()}${texto.slice(1)}` : texto)
  const cambios = Object.entries(m.cambios ?? {})
  const detalle = (excluir: string[] = []) =>
    cambios.filter(([c]) => !excluir.includes(c)).map(([c, [antes, despues]]) => `${CAMPOS[c] ?? c}: ${valor(c, antes)} → ${valor(c, despues)}`)

  switch (m.tipo) {
    case 'CARGA':
      return { titulo: conBien('Cargado desde la planilla del inventario físico'), detalle: [] }
    case 'ALTA':
      return { titulo: conBien('Registrado en el sistema'), detalle: [] }
    case 'EDICION':
      return { titulo: conBien('Datos corregidos'), detalle: detalle() }
    case 'TRASLADO': {
      const origen = m.recinto_anterior?.nombre ?? 'sin ubicación'
      const destino = m.recinto?.nombre ?? '—'
      const titulo =
        m.sentido === 'SALIDA' ? `Salió hacia ${destino}` : m.sentido === 'ENTRADA' ? `Llegó desde ${origen}` : `Trasladado de ${origen} a ${destino}`
      const reaparecio = m.cambios?.estado ? ['Estaba no ubicado: vuelve a activo'] : []
      return { titulo: conBien(titulo), detalle: reaparecio }
    }
    case 'ESTADO': {
      const [antes, nuevo] = m.cambios?.estado ?? []
      if (antes === 'BAJA') return { titulo: conBien('Baja revertida'), detalle: nuevo === 'NO_UBICADO' ? ['Vuelve como no ubicado'] : [] }
      if (nuevo === 'BAJA') return { titulo: conBien('Marcado para dar de baja'), detalle: [] }
      return { titulo: conBien(nuevo === 'NO_UBICADO' ? 'Marcado como no ubicado' : 'Ubicado de nuevo'), detalle: [] }
    }
    case 'FOTO': {
      const agregadas = Number(m.cambios?.agregadas ?? 0)
      if (agregadas) return { titulo: conBien(agregadas === 1 ? '1 foto agregada' : `${agregadas} fotos agregadas`), detalle: [] }
      return { titulo: conBien('Foto borrada'), detalle: m.cambios?.eliminada ? [String(m.cambios.eliminada)] : [] }
    }
    case 'TELETRABAJO': {
      const c = m.cambios ?? {}
      if (c.terminada) return { titulo: conBien('Ficha de teletrabajo terminada'), detalle: [`${c.terminada[0]}, hasta el ${formatoDia(String(c.terminada[1]))}`] }
      if (c.persona) {
        return {
          titulo: conBien(`Ficha de teletrabajo: ${c.persona[1]}`),
          detalle: [`Teletrabaja el ${c.dias?.[1] ?? '—'}`, `Desde el ${formatoDia(String(c.desde?.[1] ?? ''))}`],
        }
      }
      const lineas = cambios.map(([campo, [antes, despues]]) =>
        campo === 'dias' ? `Días: ${antes} → ${despues}` : `${CAMPOS[campo] ?? campo}: ${valor(campo, antes)} → ${valor(campo, despues)}`,
      )
      return { titulo: conBien('Ficha de teletrabajo cambiada'), detalle: lineas }
    }
    case 'ENCARGADO': {
      const [antes, despues] = m.cambios?.encargado ?? [null, null]
      return { titulo: `Encargado: ${valor('encargado', antes)} → ${valor('encargado', despues)}`, detalle: [] }
    }
    case 'RECINTO':
      if (m.cambios?.codigo) return { titulo: 'Recinto creado', detalle: [] }
      if (!cambios.length) return { titulo: 'Recinto cargado desde la planilla', detalle: [] }
      return { titulo: 'Datos del recinto', detalle: detalle() }
    case 'PLANCHETA': {
      const [antes, despues] = m.cambios?.plancheta_estado ?? ['', '']
      return { titulo: `Plancheta: ${valor('plancheta_estado', antes)} → ${valor('plancheta_estado', despues)}`, detalle: [] }
    }
    case 'IMPRESION':
      return { titulo: 'Plancheta impresa', detalle: [] }
    default:
      return { titulo: m.tipo, detalle: detalle() }
  }
}
